import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { SurfaceProvider } from '@/theme';
import type { SurfaceLevel, ResolvedTheme } from '@/theme';

const surfaceVariants = cva(
  'rounded-lg transition-colors',
  {
    variants: {
      level: {
        base: 'bg-background text-foreground',
        surface1: 'bg-surface-1 text-foreground shadow-sm',
        surface2: 'bg-surface-2 text-foreground shadow-md',
        surface3: 'bg-surface-3 text-foreground shadow-lg',
        overlay: 'bg-overlay/80 text-foreground shadow-2xl backdrop-blur-sm',
      },
      p: {
        0: 'p-0',
        1: 'p-1',
        2: 'p-2',
        3: 'p-3',
        4: 'p-4',
        5: 'p-5',
        6: 'p-6',
        8: 'p-8',
        10: 'p-10',
        12: 'p-12',
        16: 'p-16',
        20: 'p-20',
        24: 'p-24',
      },
      gap: {
        0: 'gap-0',
        1: 'gap-1',
        2: 'gap-2',
        3: 'gap-3',
        4: 'gap-4',
        5: 'gap-5',
        6: 'gap-6',
        8: 'gap-8',
        10: 'gap-10',
        12: 'gap-12',
      },
      muted: {
        true: 'opacity-60',
        false: '',
      },
    },
    defaultVariants: {
      level: 'base',
      muted: false,
    },
  }
);

export interface SurfaceProps
  extends React.HTMLAttributes<HTMLDivElement>,
    Omit<VariantProps<typeof surfaceVariants>, 'level'> {
  /** Surface elevation level (affects background color) */
  level?: SurfaceLevel;

  /** Optional theme override (force light/dark for this surface) */
  theme?: ResolvedTheme;

  /** Apply padding shorthand */
  p?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16 | 20 | 24;

  /** Apply gap shorthand */
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12;

  /** Muted state (reduced opacity) */
  muted?: boolean;
}

/**
 * Surface - Container component with elevation levels and optional theme override
 *
 * Provides background, shadow, and theme context for nested components.
 * Can force light or dark theme for specific sections (e.g., photo viewers).
 *
 * @example
 * <Surface level="surface1" p={4}>
 *   <h2>Card Title</h2>
 *   <p>Card content</p>
 * </Surface>
 *
 * @example
 * // Force light theme for photo viewer
 * <Surface level="surface1" theme="light" className="bg-[rgb(161_161_170)]">
 *   <PhotoViewer />
 * </Surface>
 */
export const Surface = forwardRef<HTMLDivElement, SurfaceProps>(
  ({ level = 'base', theme, p, gap, muted, className, children, ...props }, ref) => {
    return (
      <SurfaceProvider level={level} theme={theme}>
        <div
          ref={ref}
          className={cn(surfaceVariants({ level, p, gap, muted }), className)}
          {...props}
        >
          {children}
        </div>
      </SurfaceProvider>
    );
  }
);

Surface.displayName = 'Surface';
